import React, { useState, useEffect, useContext } from 'react';
import './ShareModal.css';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, query, where, getDoc, doc } from 'firebase/firestore';
import { UserContext } from '../../context/UserContext';

const ShareModal = ({ isOpen, onClose, post, onShare }) => {
    const { user } = useContext(UserContext);
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState('');
    const [caption, setCaption] = useState('');
    const [loading, setLoading] = useState(false);
    const [userName, setUserName] = useState('');

    useEffect(() => {
        if (isOpen && user) {
            fetchConversations();
            fetchUserName();
        }
    }, [isOpen, user]);

    const fetchUserName = async () => {
        try {
            const userRef = doc(db, "UserInformation", user.uid);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
                setUserName(userDoc.data().name);
            }
        } catch (error) {
            console.error('Error fetching user name:', error);
        }
    };

    const fetchConversations = async () => {
        try {
            const conversationsRef = collection(db, 'conversations');
            const q = query(conversationsRef, where('participants', 'array-contains', user.uid));
            const querySnapshot = await getDocs(q);
            
            const conversationsList = await Promise.all(querySnapshot.docs.map(async (docSnap) => {
                const chatData = docSnap.data();
                let chatName = 'Unnamed Conversation';
                
                if (!chatData.isGroup) {
                    const otherUserId = chatData.participants.find(id => id !== user.uid);
                    if (otherUserId) {
                        const otherUserRef = doc(db, "UserInformation", otherUserId);
                        const otherUserDoc = await getDoc(otherUserRef);
                        if (otherUserDoc.exists()) {
                            chatName = otherUserDoc.data().name;
                        }
                    }
                } else {
                    try {
                        if (chatData.groupId) {
                            const groupRef = doc(db, "groups", chatData.groupId);
                            const groupDoc = await getDoc(groupRef);
                            if (groupDoc.exists()) {
                                chatName = groupDoc.data().title;
                            }
                        }
                        else {
                            const groupsRef = collection(db, "groups");
                            const groupQuery = query(groupsRef, where("conversationId", "==", docSnap.id));
                            const groupSnapshot = await getDocs(groupQuery);
                            if (!groupSnapshot.empty) {
                                const groupDoc = groupSnapshot.docs[0];
                                chatName = groupDoc.data().title;
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching group title:', error);
                    }
                }
                
                return {
                    id: docSnap.id,
                    name: chatName,
                    isGroup: chatData.isGroup,
                    ...chatData
                };
            }));
            
            const sortedConversations = conversationsList.sort((a, b) => {
                if (a.isGroup && !b.isGroup) return -1;
                if (!a.isGroup && b.isGroup) return 1;
                return a.name.localeCompare(b.name);
            });
            
            setConversations(sortedConversations);
        } catch (error) {
            console.error('Error fetching conversations:', error);
        }
    };

    const handleShare = async () => {
        if (!selectedConversation || !caption.trim()) {
            alert('Please select a conversation and add a caption');
            return;
        }

        setLoading(true);
        try {
            const postId = post.id;
            if (!postId) throw new Error('Invalid post id');

            const messagesRef = collection(db, 'conversations', selectedConversation, 'messages');
            await addDoc(messagesRef, {
                type: 'shared_post',
                postId: postId,
                caption: caption,
                timestamp: new Date(),
                senderId: user.uid,
                senderName: userName
            });

            await onShare();
            onClose();
        } catch (error) {
            console.error('Error sharing post:', error);
            alert('Failed to share the post. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="share-modal-overlay">
            <div className="share-modal">
                <div className="share-modal-header">
                    <h2>Share Video</h2>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>
                
                <div className="share-modal-content">
                    <div className="conversation-selector">
                        <label htmlFor="conversation">Select Conversation:</label>
                        <select
                            id="conversation"
                            value={selectedConversation}
                            onChange={(e) => setSelectedConversation(e.target.value)}
                        >
                            <option value="">Select a conversation</option>
                            {conversations.map(conv => (
                                <option key={conv.id} value={conv.id}>
                                    {conv.isGroup ? `👥 ${conv.name}` : `👤 ${conv.name}`}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="caption-input">
                        <label htmlFor="caption">Add Caption:</label>
                        <textarea
                            id="caption"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            placeholder="Write a caption..."
                            rows="3"
                        />
                    </div>

                    <div className="share-modal-footer">
                        <button 
                            className="share-button" 
                            onClick={handleShare}
                            disabled={loading}
                        >
                            {loading ? 'Sharing...' : 'Share'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareModal; 